import cx from 'classnames';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FunctionComponent,
} from 'react';
import { useIntl } from 'react-intl';

import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { disableNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/disable-native-drag-preview';
import { preventUnhandled } from '@atlaskit/pragmatic-drag-and-drop/prevent-unhandled';
import {
  useSplitScreenResize,
  useSplitScreenSharedState,
  type PanelNames,
} from '@trello/split-screen';
import { getProposedPanelWidth } from '@trello/split-screen/';
import type { SplitScreenTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import styles from './CollapsibleSplitScreenResizeDivider.less';

interface CollapsibleSplitScreenResizeDividerProps {
  enableTransitionsCallback: (enableTransitions: boolean) => void;
  isCollapsedCallback: (isCollapsed: boolean) => void;
  isPanelHidden: boolean;
  panelName: PanelNames;
  panelWidthCallback: (width: number) => void;
}

export const CollapsibleSplitScreenResizeDivider: FunctionComponent<
  CollapsibleSplitScreenResizeDividerProps
> = ({
  enableTransitionsCallback,
  isCollapsedCallback,
  isPanelHidden,
  panelName,
  panelWidthCallback,
}) => {
  const intl = useIntl();
  const dividerRef = useRef<HTMLDivElement>(null);

  const {
    storedPanelWidth,
    previousPanelWidth,
    collapsePanel,
    updatePanelSize,
  } = useSplitScreenResize({
    panelName,
  });

  const { resizeAllPanelsToDefault } = useSplitScreenSharedState();

  const maxWidth = storedPanelWidth.maxWidth;
  const minWidth = storedPanelWidth.minWidth;
  const [isDragging, setIsDragging] = useState(false);

  const handleDividerDoubleClick = useCallback(() => {
    resizeAllPanelsToDefault();
    // panelWidthCallback(defaultPanelWidths[panelName].width);
  }, [resizeAllPanelsToDefault]);

  useEffect(() => {
    const divider = dividerRef.current;

    if (!divider) {
      return;
    }

    return draggable({
      element: divider,
      onGenerateDragPreview: ({ nativeSetDragImage }) => {
        disableNativeDragPreview({ nativeSetDragImage });
        preventUnhandled.start();
      },
      onDragStart() {
        enableTransitionsCallback(false);
        setIsDragging(true);
      },
      onDrag({ location }) {
        const newWidth = getProposedPanelWidth({
          location,
          maxWidth,
          minWidth,
          previousWidth: previousPanelWidth,
        });

        const shouldEnableTransitions = newWidth === 0 || newWidth === minWidth;

        enableTransitionsCallback(shouldEnableTransitions);

        panelWidthCallback(newWidth);

        isCollapsedCallback(newWidth === 0);
      },
      onDrop({ location }) {
        preventUnhandled.stop();

        const newWidth = getProposedPanelWidth({
          location,
          maxWidth,
          minWidth,
          previousWidth: previousPanelWidth,
        });
        enableTransitionsCallback(true);

        if (newWidth === 0) {
          collapsePanel();
          isCollapsedCallback(true);
        } else {
          updatePanelSize(newWidth);
        }

        setIsDragging(false);
      },
    });
  }, [
    collapsePanel,
    enableTransitionsCallback,
    isCollapsedCallback,
    maxWidth,
    minWidth,
    panelWidthCallback,
    previousPanelWidth,
    updatePanelSize,
  ]);

  if (panelName === 'board') {
    return null;
  }

  return (
    <div className={styles.dragAndDropDividerContainer}>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions*/}
      <div
        className={cx(styles.dragAndDropDivider, {
          [styles.dragging]: isDragging,
          [styles.hidden]: isPanelHidden,
        })}
        aria-label={intl.formatMessage({
          id: 'templates.split-screen-resize.drag-to-resize',
          defaultMessage: 'Drag to resize',
          description: 'Aria label for the resize divider',
        })}
        role="separator"
        ref={dividerRef}
        data-testid={getTestId<SplitScreenTestIds>('resize-divider')}
        onDoubleClick={handleDividerDoubleClick}
      />
    </div>
  );
};
